// ─── Attack Path Hooks ──────────────────────────────────────────────────
// TanStack Query hooks for attack path data.

import { get } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useQuery } from '@tanstack/react-query';
import type { AttackPathResponse, AttackPathSummary, IncidentNarrative } from '@uncain/shared';

interface AttackPathsData {
	readonly paths: readonly AttackPathResponse[];
	readonly summary: AttackPathSummary;
}

export function useAttackPaths(params?: { minScore?: number; limit?: number }) {
	const queryParams = new URLSearchParams();
	if (params?.minScore != null) queryParams.set('minScore', String(params.minScore));
	if (params?.limit != null) queryParams.set('limit', String(params.limit));
	const qs = queryParams.toString();
	const url = qs ? `/attack-paths?${qs}` : '/attack-paths';

	return useQuery<AttackPathsData>({
		queryKey: queryKeys.attackPaths.list(params),
		queryFn: () => get<AttackPathsData>(url),
	});
}

export function useIncidentAttackPaths(incidentId: string) {
	return useQuery<AttackPathsData>({
		queryKey: queryKeys.attackPaths.forIncident(incidentId),
		queryFn: () => get<AttackPathsData>(`/attack-paths/incidents/${incidentId}`),
		enabled: !!incidentId,
	});
}

export function useIncidentNarrative(incidentId: string) {
	return useQuery<IncidentNarrative>({
		queryKey: queryKeys.narrative.forIncident(incidentId),
		queryFn: () => get<IncidentNarrative>(`/incidents/${incidentId}/narrative`),
		enabled: !!incidentId,
	});
}
